import {Injectable} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {AUTH0_ROLE, UserProfile} from '../shared/types/UserProfile';

@Injectable({
  providedIn: 'root'
})

export class AuthorizationService {

  constructor() {
  }

  private _userProfile = new BehaviorSubject<UserProfile>({
    id: '', email: '', firstName: '', lastName: '', role: AUTH0_ROLE.NONE, account: '', trialDaysLeft: null
  });
  private _permissions = new BehaviorSubject<string[]>([]);
  public userProfile$ = this._userProfile.asObservable();
  public permissions$ = this._permissions.asObservable();

  get userProfile(): UserProfile {
    return this._userProfile.getValue();
  }

  set userProfile(profile: Partial<UserProfile>) {
    this._userProfile.next({...this.userProfile, ...profile});
  }

  get profileInitiated(): boolean {
    return Boolean(this.userProfile.email || this.userProfile.firstName);
  }

  set permissions(permissions: string[]) {
    this._permissions.next(permissions);
  }

  resetProfile() {
    // Explicit undefineds: the setter merges partials, so the subscription signal must be
    // overwritten here or it would leak across logins in the same browser session.
    this.userProfile = {id: '', email: '', firstName: '', lastName: '', role: AUTH0_ROLE.NONE, account: '', subscriptionStatus: undefined, trialEndsAt: undefined};
  }

  hasPermissions(permissions: string[]): boolean {
    return permissions.every(p => this._permissions.getValue().includes(p));
  }

  get isAdminUser(): boolean {
    return this.userProfile.role === AUTH0_ROLE.ADMIN;
  }

  // The account-level subscriptionStatus (effective, from GET /users/my-user) is authoritative
  // when present; the Auth0 role stays as the fallback so this client remains independently
  // deployable and rollback-able against a pre-redesign server.
  get isTrialUser(): boolean {
    const status = this.userProfile.subscriptionStatus;
    return status ? status === 'TRIALING' : this.userProfile.role === AUTH0_ROLE.TRIAL;
  }

  get isExpiredUser(): boolean {
    const status = this.userProfile.subscriptionStatus;
    return status ? status === 'EXPIRED' : this.userProfile.role === AUTH0_ROLE.EXPIRED;
  }
}
